import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "next-themes";
import DashboardContainer from "./DashboardContainer";
import Sidebar from "./dashboard/Sidebar";
import MobileHeader from "./dashboard/MobileHeader";
import DashboardWalletButton from "./wallet-btn/DashboardWalletButton";
import { NotificationProvider } from "../contexts/NotificationContext";
import DarkMode from "./mode/DarkMode";

import RoleGuard from "./roles/RoleGuard";

const DashboardLayout = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();
  const { user } = useAuth();

  // Handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      // Auto-close sidebar on mobile
      if (mobile) {
        setIsSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const setSidebarExpanded = (expanded) => {
    setIsSidebarExpanded(expanded);
  };

  const closeSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <NotificationProvider>
      <div className="flex h-screen bg-light-base dark:bg-jacarta-800">
        {/* Mobile Header */}
        {isMobile && (
          <MobileHeader
            onToggleSidebar={toggleSidebar}
            isSidebarOpen={isSidebarOpen}
          />
        )}

        {/* Sidebar */}
        <Sidebar
          isOpen={isSidebarOpen}
          isExpanded={isSidebarExpanded}
          isMobile={isMobile}
          onClose={closeSidebar}
          onSetExpanded={setSidebarExpanded}
          currentPath={router.pathname}
          user={user}
        />

        {/* Mobile Overlay */}
        {isMobile && isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Desktop Overlay - when sidebar is expanded */}
        {!isMobile && (
          <div
            className={`fixed inset-0 bg-black z-30 transition-opacity duration-300 pointer-events-none ${
              isSidebarExpanded ? "opacity-30 pointer-events-auto" : "opacity-0"
            }`}
          />
        )}

        {/* Main Content */}
        <main
          className={`
          flex-1 flex flex-col overflow-hidden
          ${isMobile ? "pt-16 ml-0" : "ml-20"}
        `}
        >
          {/* Floating Wallet Button for Desktop */}
          {!isMobile && (
            <div className="absolute top-6 right-6 z-10 flex items-center gap-4">
              <DarkMode />
              <DashboardWalletButton />
            </div>
          )}

          <div className="flex-1 overflow-y-auto">
            <div className="p-6">
              <DashboardContainer>
                <RoleGuard>{children}</RoleGuard>
              </DashboardContainer>
            </div>
          </div>
        </main>
      </div>
    </NotificationProvider>
  );
};

export default DashboardLayout;
